import { useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate, useParams } from "react-router-dom";
import RMLogo from "@/assets/images/rm-logo-emblem.svg";
import { Button, Container, Typography } from "@/components/atoms";
import { ClientScopeProvider } from "@/components/pages/Client/settings/ClientScopeContext";
import { useSiteConfig } from "@/hooks";
import { useGetAccessQuery } from "@/redux/apis/assistants/assistantsApi";
import { clsx } from "@/utils";
import { ManageClientLoader } from "./ManageClientLoader";

const TABS = [
  { to: "details", label: "Client Details" },
  { to: "favourites", label: "Favourites" },
  { to: "settings", label: "Settings" },
] as const;

// Time the "Returning to Your Account" loader stays up before landing back on My Clients.
const RETURN_DELAY_MS = 1200;

/**
 * Client-portal "Manage Client" full-screen takeover (DEV-408/409). An Executive Assistant enters
 * from My Clients to act on behalf of a connected client. Reads the access-link id from `:accessId`,
 * resolves it to the client's account uuid via `useGetAccessQuery`, and gates on a CONNECTED link.
 *
 * Draws its own chrome (blue banner + logo/tabs/Stop-Managing nav) — the portal skips ClientHeader
 * for this route (see ClientPortal). The body `<Outlet/>` is wrapped in `ClientScopeProvider`
 * so every reused client screen targets the managed account. Mirrors the operator precedent at
 * `Clients/ManageClient/ManageClient.tsx`, adapted to the client portal.
 */
export const ManageClientTakeover = () => {
  const { accessId = "" } = useParams<{ accessId: string }>();
  const navigate = useNavigate();
  const { name: siteName, images } = useSiteConfig();
  const logoUrl = images.emblem || RMLogo;

  const { data: access, isLoading, isError } = useGetAccessQuery(accessId, { skip: !accessId });

  const [isReturning, setIsReturning] = useState(false);

  const clientName = access?.party.name || "";
  const accountId = access?.party.uuid ?? "";

  const handleStopManaging = () => {
    setIsReturning(true);
    window.setTimeout(() => navigate("/client/my-clients"), RETURN_DELAY_MS);
  };

  // Stop Managing — show the returning loader, then redirect (handled above).
  if (isReturning) {
    return <ManageClientLoader variant="returning" clientName={clientName} />;
  }

  // Resolving the access link — show the accessing loader.
  if (isLoading) {
    return <ManageClientLoader variant="accessing" clientName={clientName} />;
  }

  // A failed/unauthorized resolve or a non-CONNECTED link bounces back to My Clients.
  if (isError || !access || access.status !== "CONNECTED" || !accountId) {
    return <Navigate to="/client/my-clients" replace />;
  }

  return (
    <div className="flex h-screen flex-col">
      {/* Thin blue banner: "Managing {name}'s account", centered. Figma Status/Info blue (#2E90FA = bg-info), NOT brand purple. */}
      <div className="flex h-7 flex-none items-center justify-center bg-info px-4">
        <Typography variant="small" className="truncate font-semibold text-white">
          Managing {clientName}&apos;s account
        </Typography>
      </div>

      {/* Logo + tabs left, Stop Managing pushed right — matches ClientHeader's nav placement. */}
      <header className="flex-none border-b border-neutral-mid-gray bg-white">
        {/* Cap + centre at 2360 (not Container's default 1610) so the nav aligns with the widest tab
            body (Details) on ultrawide — matches the operator/DSN-558 pattern (DEV-1367). */}
        <Container className="w-full !max-w-[2360px]">
          <div className="flex h-14 items-center gap-6">
            <figure className="flex flex-none items-center gap-2">
              <img className="h-8 w-8 flex-shrink-0 object-contain" src={logoUrl} alt={siteName} />
            </figure>

            <nav aria-label="Manage client sections" className="flex h-full items-center">
              {TABS.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  className={({ isActive }) =>
                    clsx(
                      "-mb-px flex h-14 items-center justify-center px-4 text-base font-semibold leading-5",
                      isActive ? "border-b-2 border-primary text-primary-dark" : "text-neutral-silver-gray"
                    )
                  }
                >
                  {tab.label}
                </NavLink>
              ))}
            </nav>

            <Button variant="tertiary-light" size="md" startIcon="close" isCustomIcon className="ml-auto flex-none" onClick={handleStopManaging}>
              Stop Managing
            </Button>
          </div>
        </Container>
      </header>

      {/* Gray body surface; content centered. Reused screens resolve the managed account via ClientScopeProvider. */}
      <main className="flex flex-1 flex-col overflow-auto bg-neutral-surface-gray">
        <ClientScopeProvider accountId={accountId}>
          {/* Body caps + centres at 2360 (DEV-1367): the Details tab fills it (mirrors ClientHome), while
              the Favourites/Settings tabs self-cap tighter at 1164 (mirroring their standalone variants). */}
          <Container className="w-full !max-w-[2360px]">
            <Outlet />
          </Container>
        </ClientScopeProvider>
      </main>
    </div>
  );
};
